import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import RightPanel from 'components/RightPanel/RightPanel'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import SidebarPartners from 'components/Sidebars/SidebarPartners'
import SidebarRecentActivity from 'components/Sidebars/SidebarRecentActivity'
import { initializeMyLocationImages } from 'slices/locationImageSlice'
import { initializeCaptureImages } from 'slices/captureImageSlice'
import { mergeLibraryImages, type LibraryImageOrigin } from 'utils/libraryImages'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { Image } from 'lucide-react'
import { ImageLightbox } from 'components/ui/image-lightbox'
import { transformUrl, TRANSFORM } from 'components/FileUploader/fileUploadUtils'
import { DateSeparator, DateJumpPill, type TJumpTarget } from 'components/DateSeparator'
import JumpToDateDialog from 'components/JumpToDateDialog'
import { groupByDay, resolveJumpDayKey, earliestDayOnOrAfter } from 'utils/groupByDay'
import { isImageType } from 'utils/mediaType'

interface ImageItem {
	_id: string
	size: string
	type: string
	name: string
	url: string
	uuid: string
	createdAt: string
	note: string | null
	location: string | null
	project: string | null
	creator: string | null
	/** Where it came from, so a field capture can be told from an upload. */
	origin?: LibraryImageOrigin
	/** The capture's label, or the record a filed image belongs to. */
	context?: string | null
}

const ImagesGalleryCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	const { userInfo } = useSelector((state: any) => state.userLogin)
	const userId = userInfo?._id

	const [openPopup, setOpenPopup] = useState(false)
	const [recordForEdit, setRecordForEdit] = useState<ImageItem | null>(null)
	const [lightboxOpen, setLightboxOpen] = useState(false)
	const [noteValue, setNoteValue] = useState('')
	const [pickerOpen, setPickerOpen] = useState(false)
	// One node per day section, so a jump can scroll straight to it.
	const sectionRefs = useRef<Record<string, HTMLElement | null>>({})
	const scrollRef = useRef<HTMLElement | null>(null)

	const { entities: imageEntities, ids: imageIds, isInitialized, isLoading: loading } = useSelector(
		(state: any) => state.locationImages,
	)
	const { entities: captureEntities, ids: captureIds, isInitialized: capturesReady } = useSelector(
		(state: any) => state.captureImages,
	)

	const locationImages = useMemo(
		() => imageIds.map((id: string) => imageEntities[id]).filter(Boolean),
		[imageIds, imageEntities],
	)
	const captureImages = useMemo(
		() => (captureIds || []).map((id: string) => captureEntities[id]).filter(Boolean),
		[captureIds, captureEntities],
	)

	/*
		Both collections, as one list. MYD-468.

		This screen used to read `locationImages` alone, so a photograph taken on
		a phone never appeared here at all: a rep could shoot forty images in a
		car park and find none of them at a desk. The ones that did show up were
		incidental, having been pulled into a note, which writes a locationImage
		row pointing at the same file.

		`mergeLibraryImages` deduplicates by ASSET, so those incidental pairs
		appear once rather than twice, and the capture wins because it carries
		the label and the moment the shutter went.
	*/
	const library = useMemo(
		() => mergeLibraryImages(captureImages, locationImages),
		[captureImages, locationImages],
	)

	const allImages: ImageItem[] = useMemo(
		() =>
			library.map(image => ({
				_id: image.id,
				size: image.size as unknown as string,
				type: image.type as string,
				name: image.name as string,
				url: image.url,
				uuid: image.uuid as string,
				// `takenAt` rather than `createdAt`: a capture is grouped under the
				// day it was TAKEN, which is what a rep looks for and can differ
				// from when its row reached the database.
				createdAt: image.takenAt,
				note: null,
				// A capture's label, or the location or project a filed image
				// belongs to. One field, because both answer "what is this of".
				location: image.origin === 'filed' ? image.context ?? null : null,
				project: null,
				creator: null,
				origin: image.origin,
				context: image.context ?? null,
			})),
		[library],
	)

	useEffect(() => {
		// @ts-ignore
		dispatch(initializeMyLocationImages())
	}, [dispatch, userId, isInitialized])

	useEffect(() => {
		if (!capturesReady) (dispatch as any)(initializeCaptureImages(undefined))
	}, [dispatch, capturesReady])

	/*
		Built from the merged library, so the sidebar and the grid agree.

		It used to read `locationImages` alone. Once captures appear in the grid
		that becomes a visible contradiction: a photograph sitting in the day
		section, absent from the activity list beside it, on the same screen.

		`library` is already sorted newest-first and memoised, so this costs a
		map rather than a re-sort of the whole library on every keystroke
		elsewhere on the page.
	*/
	const recentImages = useMemo(
		() =>
			library.map(image => ({
				id: image.id,
				name: image.name,
				detail: image.context ?? null,
				timestamp: image.takenAt,
				imageUrl: transformUrl(image.url, TRANSFORM.THUMB),
			})),
		[library],
	)

	// Grouped for display only: the endpoint already sorts newest-first and
	// groupByDay is contiguous, so the order on screen is unchanged.
	const imageDays = groupByDay(allImages, image => image.createdAt)

	const scrollToDay = useCallback((key?: string) => {
		if (!key) return
		sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}, [])

	const handleJump = useCallback(
		(target: TJumpTarget) => {
			if (imageDays.length === 0) return

			if (target === 'pickDate') {
				setPickerOpen(true)
				return
			}

			// 'recent' scrolls the container itself rather than the newest
			// section, mirroring how the chat handles its own end of the list.
			// Aligning the first section instead would stop just below the page
			// heading, leaving the grid looking already scrolled.
			if (target === 'recent') {
				scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
				return
			}

			// The library is ordered newest-first, the opposite of the chat it
			// borrows this control from, so the remaining targets resolve against
			// a descending list.
			scrollToDay(resolveJumpDayKey(imageDays.map(day => day.key), target))
		},
		[imageDays, scrollToDay],
	)

	const handlePickDate = useCallback(
		(date: Date) => scrollToDay(earliestDayOnOrAfter(imageDays.map(day => day.key), moment(date))),
		[imageDays, scrollToDay],
	)

	const handleSelectRecentImage = (id: string) => {
		const match = allImages.find(image => image._id === id)
		if (match) handlePreviewImage(match)
	}

	const handlePreviewImage = (image: ImageItem) => {
		setRecordForEdit(image)
		setNoteValue(image.note || '')
		setOpenPopup(true)
	}

	const renderContent = () => {
		if (loading) {
			return (
				<div className="flex items-center justify-center py-20">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#0080fe]" />
				</div>
			)
		}

		if (allImages.length === 0) {
			return (
				<div className="flex flex-col items-center justify-center py-20 text-gray-400">
					<svg className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
					</svg>
					<p className="text-sm">No images uploaded yet.</p>
				</div>
			)
		}

		return (
			<>
				<div className="max-w-[1080px] mx-auto w-full mt-4 bg-white rounded-lg border border-gray-200 p-5">
				{imageDays.map(day => (
				<section
					key={day.key}
					ref={node => {
						sectionRefs.current[day.key] = node
					}}
				>
					<DateSeparator sticky>
						<DateJumpPill label={day.label} onJump={handleJump} noun="library" />
					</DateSeparator>

				<div className="grid grid-cols-4 gap-3 pb-4">
					{day.items.map(image => (
						<div
							key={image._id}
							className="group relative cursor-pointer rounded-lg overflow-hidden bg-gray-100 shadow-sm hover:shadow-lg hover:scale-[1.03] transition-all duration-150"
							onClick={() => handlePreviewImage(image)}
						>
							{isImageType(image.type) ? (
								<div
									className="w-full pt-[75%] bg-cover bg-center"
									style={{
										backgroundImage: `url(${transformUrl(image.url, TRANSFORM.THUMB)})`,
									}}
								/>
							) : (
								<video className="w-full aspect-[4/3] object-cover" src={image.url} controls />
							)}

							{/*
								A field capture says so, and says what it is of.

								Sitting always-visible rather than on hover, because the
								label is the only thing telling three near-identical
								photographs of the same frontage apart, and a rep looking
								for one of forty should not have to hover over each.
								Filed images keep the hover overlay alone: their context
								is already the section they are filed under.
							*/}
							{image.origin === 'capture' && (
								<span className="pointer-events-none absolute left-1 top-1 max-w-[calc(100%-0.5rem)] truncate rounded bg-black/55 px-1.5 py-0.5 text-[11px] font-medium text-white">
									{image.context || 'Field capture'}
								</span>
							)}

							{/* Hover overlay */}
							<div className="absolute inset-0 flex flex-col justify-center items-center bg-[#0080fe]/60 text-white text-xs p-2 text-center rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200">
								{image.origin === 'capture' && <div>Captured in the field</div>}
								{image.location && <div>Location: {image.location}</div>}
								{image.project && <div>Project: {image.project}</div>}
								<div>
									{image.origin === 'capture' ? 'Taken' : 'Created'}:{' '}
									{moment(image.createdAt).format('MM/DD/YYYY')}
								</div>
								<div className="text-xs italic text-gray-200 mt-4">Click to preview</div>
							</div>
						</div>
					))}
				</div>
				</section>
				))}
				</div>

				{/* Mounted only while open, so the lazily-loaded date picker is not
				    fetched by every visit to the library. */}
				{pickerOpen && (
					<JumpToDateDialog
						open
						onClose={() => setPickerOpen(false)}
						onPick={handlePickDate}
						earliest={imageDays[imageDays.length - 1]?.label}
						latest={imageDays[0]?.label}
						noun="library"
						emptyDayHint="If nothing was added that day, the library opens at the next day that has images."
					/>
				)}

				<Dialog open={openPopup} onClose={() => setOpenPopup(false)}>
					<DialogContent className="max-w-lg p-5">
						<DialogHeader>
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
									<Image className="w-5 h-5" />
								</div>
								<div>
									<DialogTitle>Image Preview</DialogTitle>
									<DialogDescription>Preview image details</DialogDescription>
								</div>
							</div>
						</DialogHeader>
						{recordForEdit && (
							<>
								<img
									className="w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
									src={recordForEdit.url}
									alt={recordForEdit.name}
									title="Click to zoom"
									onClick={() => setLightboxOpen(true)}
								/>
								<div className="mt-4 text-center">
									<h3 className="text-sm font-medium text-gray-500">{recordForEdit.name}</h3>
								</div>
								<div className="mt-4">
									<label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
									<textarea
										className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-[#0080fe] focus:outline-none focus:ring-1 focus:ring-[#0080fe] resize-vertical"
										rows={3}
										placeholder="Add a note..."
										value={noteValue}
										onChange={e => setNoteValue(e.target.value)}
									/>
								</div>
								<div className="flex justify-end mt-4">
									<button
										type="button"
										className="rounded-lg bg-[#0080fe] px-4 py-1.5 text-sm font-medium text-white hover:bg-[#0070e0] transition-colors"
										onClick={() => {
											// updateImageHandler placeholder
										}}
									>
										Update
									</button>
								</div>
								<ImageLightbox
									src={recordForEdit.url}
									alt={recordForEdit.note || 'Image preview'}
									open={lightboxOpen}
									onClose={() => setLightboxOpen(false)}
								/>
							</>
						)}
					</DialogContent>
				</Dialog>
			</>
		)
	}

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main
						ref={node => {
							scrollRef.current = node
						}}
						className="flex-1 overflow-y-auto p-6"
					>
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">Images</h2>
						{renderContent()}
					</main>

					<RightPanel storageKey="library-images" label="Library panel">
						<div className="flex flex-col h-1/2 border-b border-gray-200">
							<SidebarRecentActivity
								items={recentImages}
								isLoading={loading}
								emptyMessage="No images yet"
								emptyHint="Images added to a location or project show up here."
								onSelect={item => handleSelectRecentImage(item.id)}
							/>
						</div>
						<div className="flex flex-col h-1/2">
							<SidebarPartners />
						</div>
					</RightPanel>
				</div>
			</div>
		</div>
	)
}

export default ImagesGalleryCN
